'use client';

import { useState } from 'react';
import { useTranslations } from 'next-intl';

import { cn } from '@/lib/styleUtils';
import { VehicleSprite, type VehicleKind } from '@/modules/captcha/components/CarSprite';
import { PuzzleGrid, PuzzlePrompt } from '@/modules/captcha/components/PuzzleFrame';
import { VEHICLE_COLORS, randomInt, shuffle } from '@/modules/captcha/shared/puzzle-utils';
import { useTapChallenge } from '@/modules/captcha/hooks/useTapChallenge';
import { shakeAnimation, tileBase, tileCorrect, tileIdle, tileWrong } from './puzzle.style';
import type { PuzzleProps } from './puzzle.types';

const KINDS: VehicleKind[] = ['car', 'truck', 'bus'];
const TILE_COUNT = 4;

interface Vehicle {
  kind: VehicleKind;
  color: string;
}

/**
 * Builds a target vehicle plus a tile row containing exactly one copy of it.
 *
 * ## Invariants:
 * - Distractors are drawn from a pool of kind × colour combinations with the target removed, so
 *   no tile can accidentally duplicate the answer. Why: two matching tiles makes one of the two
 *   correct taps register as wrong, which reads as the puzzle being broken.
 * - The target's slot is chosen at random rather than appended. Why: a target that is always
 *   last is a fixed answer.
 */
function buildChallenge(): { target: Vehicle; tiles: Vehicle[]; answer: number } {
  const pool: Vehicle[] = [];
  KINDS.forEach((kind) => {
    VEHICLE_COLORS.slice(0, 4).forEach(({ hex }) => pool.push({ kind, color: hex }));
  });

  const shuffled = shuffle(pool);
  const target = shuffled[0];
  const distractors = shuffled
    .slice(1)
    .filter((v) => !(v.kind === target.kind && v.color === target.color))
    .slice(0, TILE_COUNT - 1);

  const answer = randomInt(TILE_COUNT);
  const tiles = [...distractors.slice(0, answer), target, ...distractors.slice(answer)];

  return { target, tiles, answer };
}

/** Design 2h — tap the tile showing the same vehicle as the one above. */
export const MatchTheCar = ({ onSolved }: PuzzleProps) => {
  const t = useTranslations('Captcha');
  const [{ target, tiles, answer }] = useState(buildChallenge);
  const { wrongIndex, solved, pick } = useTapChallenge(answer, onSolved);

  return (
    <>
      <PuzzlePrompt>{t('matchTheCarPrompt')}</PuzzlePrompt>
      <div
        className="tw:mx-auto tw:flex tw:w-[312px] tw:items-center tw:justify-center tw:rounded-8 tw:bg-gray-lighter tw:p-micro"
        data-cy="captcha-match-target"
      >
        <VehicleSprite kind={target.kind} color={target.color} width={56} />
      </div>
      <PuzzleGrid columns={TILE_COUNT} data-cy="captcha-match-the-car">
        {tiles.map((tile, index) => {
          const isWrong = index === wrongIndex;
          const isCorrect = solved && index === answer;
          return (
            <button
              key={`${tile.kind}-${tile.color}-${index}`}
              type="button"
              onClick={() => pick(index)}
              className={cn(
                tileBase,
                'tw:h-[52px]',
                !isWrong && !isCorrect && tileIdle,
                isCorrect && tileCorrect,
                isWrong && cn(tileWrong, shakeAnimation)
              )}
              aria-label={t('vehicleTileLabel', { index: index + 1 })}
              data-cy={`captcha-match-tile-${index}`}
            >
              <VehicleSprite kind={tile.kind} color={tile.color} />
            </button>
          );
        })}
      </PuzzleGrid>
    </>
  );
};

MatchTheCar.displayName = 'MatchTheCar';
